import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api, { fmt, fmtDate, fmtQty, daysUntil } from '../../utils/api';
import { aggregateByModel } from '../../utils/modelKey';
import * as XLSX from 'xlsx';

export default function PortalPage() {
  const { t, i18n } = useTranslation();
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const isCustomer = user?.role === 'customer';

  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  // Dashboard data
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);

  // Sub-tab data (order-based)
  const [shipped, setShipped] = useState([]);
  const [future, setFuture] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [credits, setCredits] = useState({ credits: [], totalCredits: 0 });
  const [rebate, setRebate] = useState(null);

  // Admin: reservations
  const [reservations, setReservations] = useState([]);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [resForm, setResForm] = useState({ orderNo: '', sku: '', description: '', reservedQty: '', transportMode: '', expectedArrival: '', notes: '' });
  const [resSubmitting, setResSubmitting] = useState(false);

  // Admin: shipping edit
  const [editingShipping, setEditingShipping] = useState(null); // { orderNumber, estimatedArrival, shippingMethod }

  // Dashboard filter
  const [dashFilter, setDashFilter] = useState('all');

  // Price list
  const [priceList, setPriceList] = useState([]);
  const [priceSearch, setPriceSearch] = useState('');

  // FC Orders
  const [fcOrders, setFcOrders] = useState([]);
  const [fcEdits, setFcEdits] = useState({}); // { itemId: qty }
  const [selectedFcs, setSelectedFcs] = useState(new Set());

  // RMA
  const [rmaRequests, setRmaRequests] = useState([]);
  const [rmaItems, setRmaItems] = useState([{ sku: '', description: '', quantity: 1, reason: '' }]);
  const [rmaNotes, setRmaNotes] = useState('');
  const [rmaSubmitting, setRmaSubmitting] = useState(false);

  const fetchData = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const [ordersRes, summaryRes, shippedRes, futureRes, creditsRes, rebateRes] = await Promise.all([
        api.get('/portal/orders'),
        api.get('/portal/summary'),
        api.get('/portal/shipped'),
        api.get('/portal/future'),
        api.get('/portal/credits'),
        api.get('/portal/rebate'),
      ]);
      setItems(ordersRes.data.items || []);
      setSummary(summaryRes.data);
      setShipped(shippedRes.data.orders || []);
      setFuture(futureRes.data.orders || []);
      setCredits(creditsRes.data || { credits: [], totalCredits: 0 });
      setRebate(rebateRes.data);

      if (isAdmin) {
        const [resRes, availRes] = await Promise.all([
          api.get('/portal/reservations'),
          api.get('/portal/available-orders'),
        ]);
        setReservations(resRes.data || []);
        setAvailableOrders(availRes.data || []);
      }
    } catch (err) {
      console.error('Portal fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (tab === 'price-list' && priceList.length === 0) {
      api.get('/portal/price-list').then(r => setPriceList(r.data || [])).catch(() => {});
    }
    if (tab === 'future') {
      api.get('/portal/fc-orders').then(r => setFcOrders(r.data || [])).catch(() => {});
    }
    if (tab === 'rma') {
      api.get('/portal/rma').then(r => setRmaRequests(r.data || [])).catch(() => {});
    }
  }, [tab, priceList.length]);

  // Reservation helpers
  const uniqueOrders = [...new Set(availableOrders.map(o => o.order_no))];
  const skusForOrder = availableOrders.filter(o => o.order_no === resForm.orderNo);

  const handleOrderSelect = (orderNo) => {
    setResForm(prev => ({ ...prev, orderNo, sku: '', description: '', transportMode: '', expectedArrival: '' }));
  };
  const handleSkuSelect = (sku) => {
    const match = skusForOrder.find(o => o.sku === sku);
    setResForm(prev => ({ ...prev, sku, description: match?.description || '', transportMode: match?.transport_mode || '', expectedArrival: match?.arrival_date ? match.arrival_date.split('T')[0] : '' }));
  };
  const handleReserve = async (e) => {
    e.preventDefault();
    if (!resForm.orderNo || !resForm.sku || !resForm.reservedQty) return;
    setResSubmitting(true);
    try {
      await api.post('/portal/reserve', { orderNo: resForm.orderNo, sku: resForm.sku, description: resForm.description, reservedQty: parseFloat(resForm.reservedQty), transportMode: resForm.transportMode || null, expectedArrival: resForm.expectedArrival || null, notes: resForm.notes || null });
      setResForm({ orderNo: '', sku: '', description: '', reservedQty: '', transportMode: '', expectedArrival: '', notes: '' });
      await fetchData(true);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    } finally { setResSubmitting(false); }
  };
  const handleDeleteReservation = async (id) => {
    if (!window.confirm('Delete this reservation?')) return;
    try { await api.delete(`/portal/reserve/${id}`); await fetchData(true); } catch (err) { alert('Failed: ' + err.message); }
  };

  const handleUpdateShipping = async (orderNumber) => {
    if (!editingShipping) return;
    try {
      await api.put(`/warehouse/cellcom-orders/${orderNumber}/shipping`, {
        estimatedArrival: editingShipping.estimatedArrival || null,
        shippingMethod: editingShipping.shippingMethod || null,
      });
      setEditingShipping(null);
      await fetchData(true);
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
  };
  const handleUnship = async (orderNumber) => {
    if (!window.confirm('Cancel shipped status for order #' + orderNumber + '? It will revert to "picked".')) return;
    try {
      await api.put(`/warehouse/cellcom-orders/${orderNumber}/unship`);
      await fetchData(true);
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
  };

  const he = i18n.language === 'he';

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;
  const tabs = [
    { key: 'dashboard', label: t('portal.dashboard') },
    { key: 'cellcom-orders', label: t('portal.orders') },
    { key: 'future', label: he ? 'הזמנות FC' : 'FC Orders' },
    { key: 'credits', label: t('portal.credits') },
    { key: 'rebate', label: t('portal.rebate') },
    { key: 'price-list', label: he ? 'מחירון' : 'Price List' },
    { key: 'rma', label: he ? 'בקשת RMA' : 'RMA Request' },
  ];

  return (
    <div>
      {/* Sub-tabs */}
      <div className="sub-tabs">
        {tabs.map(tb => (
          <button key={tb.key} className={`sub-tab ${tab === tb.key ? 'active' : ''}`} onClick={() => setTab(tb.key)}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* ═══ Dashboard ═══ */}
      {tab === 'dashboard' && (
        <>
          {summary && (
            <div className="summary-cards" style={{ marginBottom: 24 }}>
              {[
                { key: 'all', label: he ? 'סה"כ יחידות בהזמנות' : 'Total Units in Orders', value: fmtQty(summary.totalUnits), cls: 'gold' },
                { key: 'processing', label: he ? 'בתהליך' : 'In Processing', value: fmtQty(summary.inProcessing), cls: 'info' },
                { key: 'transit', label: he ? 'בדרך' : 'In Transit', value: fmtQty(summary.inTransit), cls: 'success' },
                { key: 'future', label: he ? 'הזמנה עתידית' : 'Future Order', value: fmtQty(summary.future), cls: '', customColor: '#e65100' },
                { key: 'next', label: he ? 'הגעה הבאה' : 'Next Arrival', value: summary.nextArrival ? fmtDate(summary.nextArrival) : '--', cls: '', small: true },
              ].map(c => (
                <div key={c.key} className={`summary-card ${c.cls} clickable`}
                  onClick={() => setDashFilter(f => f === c.key ? 'all' : c.key)}
                  style={{ cursor: 'pointer', border: dashFilter === c.key && c.key !== 'all' ? '2px solid #333' : undefined, transition: 'all 0.15s', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 8, right: 10, fontSize: 10, color: 'var(--text-muted)', opacity: 0.5 }}>ⓘ</div>
                  <div className="label" style={c.customColor ? { color: c.customColor } : {}}>{c.label}</div>
                  <div className="value" style={c.small ? { fontSize: 16 } : c.customColor ? { color: c.customColor } : {}}>{c.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Financial cubes */}
          {summary && (
            <div className="summary-cards" style={{ marginBottom: 24, gridTemplateColumns: 'repeat(5, 1fr)' }}>
              {[
                { key: 'fin-all', label: he ? 'סה"כ ערך הזמנות' : 'Total Order Value', value: fmt(summary.totalOrderValue || 0), color: '#1565c0' },
                { key: 'fin-paid', label: he ? 'שולם' : 'Paid', value: fmt(summary.totalPaid || 0), color: '#1565c0' },
                { key: 'fin-due30', label: he ? 'תשלום ≤ 30 יום' : 'Due ≤ 30 Days', value: fmt(summary.due30 || 0), color: '#16a34a' },
                { key: 'fin-due60', label: he ? 'תשלום 30-60 יום' : 'Due 30-60 Days', value: fmt(summary.due60 || 0), color: '#f59e0b' },
                { key: 'fin-due90', label: he ? 'תשלום 60-90 יום' : 'Due 60-90 Days', value: fmt(summary.due90 || 0), color: '#dc2626' },
              ].map(c => (
                <div key={c.key} className="summary-card clickable"
                  onClick={() => setDashFilter(f => f === c.key ? 'all' : c.key)}
                  style={{ cursor: 'pointer', borderLeft: `4px solid ${c.color}`, border: dashFilter === c.key ? '2px solid #333' : undefined, transition: 'all 0.15s', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 8, right: 10, fontSize: 10, color: 'var(--text-muted)', opacity: 0.5 }}>ⓘ</div>
                  <div className="label">{c.label}</div>
                  <div className="value" style={{ fontSize: 18, color: c.color }}>{c.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Financial detail panel */}
          {dashFilter.startsWith('fin-') && summary?.paymentDetails && (() => {
            const details = summary.paymentDetails;
            const filterKey = dashFilter;
            const filtered = filterKey === 'fin-all' ? details
              : filterKey === 'fin-paid' ? details.filter(d => d.paid > 0)
              : filterKey === 'fin-due30' ? details.filter(d => d.due30 > 0)
              : filterKey === 'fin-due60' ? details.filter(d => d.due60 > 0)
              : filterKey === 'fin-due90' ? details.filter(d => d.payments.some(p => p.status === 'due90'))
              : details;
            return (
              <div className="card" style={{ padding: 16, marginBottom: 20 }}>
                <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>{he ? 'הזמנה' : 'Order'}</th>
                      <th>{he ? 'שינוע' : 'Transport'}</th>
                      <th className="text-end">{he ? 'ערך' : 'Value'}</th>
                      <th>{he ? 'תשלומים' : 'Payments'}</th>
                      <th className="text-end">{he ? 'שולם' : 'Paid'}</th>
                      <th className="text-end">{he ? 'יתרה' : 'Outstanding'}</th>
                    </tr></thead>
                    <tbody>
                      {filtered.map(d => (
                        <tr key={d.orderNumber}>
                          <td className="fw-bold">#{d.orderNumber}</td>
                          <td><span className={`badge ${d.transport === 'AIR' ? 'badge-air' : 'badge-sea'}`}>{d.transport === 'AIR' ? (he ? 'אוויר' : 'Air') : (he ? 'ים' : 'Ocean')}</span></td>
                          <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmt(d.orderValue)}</td>
                          <td>
                            {d.payments.map((p, i) => (
                              <div key={i} style={{ fontSize: 12, marginBottom: 2 }}>
                                <span style={{
                                  padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                  background: p.status === 'paid' ? '#dcfce7' : p.status === 'due30' ? '#fef3c7' : p.status === 'due60' ? '#ffedd5' : '#f1f5f9',
                                  color: p.status === 'paid' ? '#166534' : p.status === 'due30' ? '#92400e' : p.status === 'due60' ? '#9a3412' : '#475569',
                                }}>{p.status === 'paid' ? (he ? 'שולם' : 'PAID') : p.dueDate ? (he ? `תשלום עד: ${fmtDate(p.dueDate)}` : `Pay by: ${fmtDate(p.dueDate)}`) : (he ? 'טרם נקבע' : 'TBD')}</span>
                                <span className="text-muted" style={{ marginInlineStart: 6, fontSize: 11 }}>{p.label}</span>
                                <span style={{ marginInlineStart: 6, fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600 }}>{fmt(p.amount)}</span>
                                {p.dueDate && p.status !== 'paid' && (() => {
                                  const days = Math.ceil((new Date(p.dueDate) - new Date()) / 86400000);
                                  return <span style={{ marginInlineStart: 6, fontSize: 10, color: days <= 7 ? '#dc2626' : days <= 14 ? '#f59e0b' : '#6b7280' }}>({days}d)</span>;
                                })()}
                              </div>
                            ))}
                            {d.payments.length === 0 && <span className="text-muted" style={{ fontSize: 11 }}>{he ? 'טרם נקבע' : 'No dates set'}</span>}
                          </td>
                          <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: '#16a34a' }}>{d.paid > 0 ? fmt(d.paid) : '—'}</td>
                          <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: d.isDelivered ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                            {d.isDelivered ? '✅' : fmt(d.orderValue - d.paid)}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 700, borderTop: '2px solid #dee2e6' }}>
                        <td colSpan={2}>{t('common.total')}</td>
                        <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmt(filtered.reduce((s, d) => s + d.orderValue, 0))}</td>
                        <td></td>
                        <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: '#16a34a' }}>{fmt(filtered.reduce((s, d) => s + d.paid, 0))}</td>
                        <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: '#dc2626' }}>{fmt(filtered.reduce((s, d) => s + d.orderValue - d.paid, 0))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* All orders — expandable */}
          {(() => {
            const allOrdersRaw = [...shipped, ...future];
            const allOrders = dashFilter === 'all' ? allOrdersRaw
              : dashFilter === 'processing' ? allOrdersRaw.filter(o => ['collecting','delivered_ff'].includes(o.customerStatus))
              : dashFilter === 'transit' ? allOrdersRaw.filter(o => o.customerStatus === 'shipped_customer')
              : dashFilter === 'future' ? allOrdersRaw.filter(o => !o.customerStatus || o.customerStatus === 'pending' || o.customerStatus === 'confirmed')
              : dashFilter === 'next' ? allOrdersRaw.filter(o => o.estimatedArrival && o.customerStatus !== 'delivered').sort((a, b) => new Date(a.estimatedArrival) - new Date(b.estimatedArrival)).slice(0, 1)
              : dashFilter === 'fin-paid' ? allOrdersRaw.filter(o => o.customerStatus === 'delivered')
              : dashFilter === 'fin-due30' || dashFilter === 'fin-due60' ? allOrdersRaw.filter(o => o.customerStatus !== 'delivered' && o.shippingDate)
              : dashFilter === 'fin-due90' ? allOrdersRaw.filter(o => o.customerStatus !== 'delivered')
              : dashFilter === 'fin-all' ? allOrdersRaw
              : allOrdersRaw;
            const toggleOrder = (num) => setExpanded(prev => { const s = new Set(prev); s.has(num) ? s.delete(num) : s.add(num); return s; });

            return allOrders.length === 0 ? (
              <div className="empty-state" style={{ padding: 40 }}>{t('warehouse.noOrders')}</div>
            ) : (
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <input className="input" placeholder={he ? 'חפש מוצר, מק"ט...' : 'Search product, SKU...'} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ maxWidth: 400 }} />
                </div>

                {/* Search result buckets */}
                {searchTerm.trim() && (() => {
                  const searchLower = searchTerm.trim().toLowerCase();
                  const buckets = { d14: 0, d30: 0, d60: 0, delivered: 0, items: [] };
                  for (const order of allOrders) {
                    for (const item of (order.items || [])) {
                      const text = `${item.sku || ''} ${item.description || ''}`.toLowerCase();
                      if (!text.includes(searchLower)) continue;
                      const qty = item.quantity || 0;
                      const eta = order.estimatedArrival;
                      const days = eta ? daysUntil(new Date(eta)) : null;
                      const isDelivered = order.customerStatus === 'delivered';
                      if (isDelivered) { buckets.delivered += qty; }
                      else if (days !== null && days <= 14) { buckets.d14 += qty; }
                      else if (days !== null && days <= 30) { buckets.d30 += qty; }
                      else { buckets.d60 += qty; }
                      buckets.items.push({ ...item, orderNumber: order.orderNumber, eta, days, isDelivered });
                    }
                  }
                  const total = buckets.delivered + buckets.d14 + buckets.d30 + buckets.d60;
                  if (total === 0) return <div className="text-muted" style={{ marginBottom: 16 }}>{he ? 'אין תוצאות' : 'No results'}</div>;
                  return (
                    <div style={{ marginBottom: 20 }}>
                      <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 12 }}>
                        {buckets.delivered > 0 && (
                          <div className="summary-card" style={{ borderLeft: '4px solid #16a34a' }}>
                            <div className="label" style={{ color: '#16a34a' }}>{he ? 'נמסר' : 'Delivered'}</div>
                            <div className="value" style={{ color: '#16a34a' }}>{fmtQty(buckets.delivered)}</div>
                            <div className="sub">{he ? 'יחידות' : 'units'}</div>
                          </div>
                        )}
                        <div className="summary-card" style={{ borderLeft: '4px solid #1565c0' }}>
                          <div className="label" style={{ color: '#1565c0' }}>0–14 {he ? 'ימים' : 'Days'}</div>
                          <div className="value" style={{ color: '#1565c0' }}>{fmtQty(buckets.d14)}</div>
                          <div className="sub">{he ? 'יחידות' : 'units'}</div>
                        </div>
                        <div className="summary-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                          <div className="label" style={{ color: '#f59e0b' }}>14–30 {he ? 'ימים' : 'Days'}</div>
                          <div className="value" style={{ color: '#f59e0b' }}>{fmtQty(buckets.d30)}</div>
                          <div className="sub">{he ? 'יחידות' : 'units'}</div>
                        </div>
                        <div className="summary-card" style={{ borderLeft: '4px solid #dc2626' }}>
                          <div className="label" style={{ color: '#dc2626' }}>30–60 {he ? 'ימים' : 'Days'}</div>
                          <div className="value" style={{ color: '#dc2626' }}>{fmtQty(buckets.d60)}</div>
                          <div className="sub">{he ? 'יחידות' : 'units'}</div>
                        </div>
                      </div>
                      <div className="table-wrap">
                        <table>
                          <thead><tr>
                            <th>{he ? 'הזמנה' : 'Order'}</th>
                            <th>{he ? 'מק"ט' : 'SKU'}</th>
                            <th>{he ? 'מוצר' : 'Product'}</th>
                            <th className="text-end">{he ? 'כמות' : 'Qty'}</th>
                            <th className="text-end">ETA</th>
                          </tr></thead>
                          <tbody>
                            {buckets.items.map((item, i) => (
                              <tr key={i}>
                                <td className="fw-bold">#{item.orderNumber}</td>
                                <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{item.sku}</td>
                                <td>{item.description}</td>
                                <td className="text-end fw-bold">{fmtQty(item.quantity)}</td>
                                <td className="text-end">
                                  {item.isDelivered ? (
                                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#16a34a', color: '#fff' }}>{he ? 'נמסר' : 'Delivered'}</span>
                                  ) : item.eta ? (
                                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                      background: item.days <= 14 ? '#dbeafe' : item.days <= 30 ? '#fef3c7' : '#fee2e2',
                                      color: item.days <= 14 ? '#1e40af' : item.days <= 30 ? '#92400e' : '#991b1b' }}>
                                      {fmtDate(item.eta)} · {item.days}d
                                    </span>
                                  ) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {allOrders
                  .filter(o => !searchTerm || o.items.some(i =>
                    i.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    i.sku.toLowerCase().includes(searchTerm.toLowerCase())
                  ))
                  .map(o => {
                    const isOpen = expanded.has(o.orderNumber);
                    return (
                      <div key={o.orderNumber} className="order-group" style={{ marginBottom: 8 }}>
                        <div className="order-group-header" onClick={() => toggleOrder(o.orderNumber)}
                          style={{ cursor: 'pointer', display: 'grid', gridTemplateColumns: '20px 110px 90px 65px 110px 180px 1fr 180px 110px', gap: 8, alignItems: 'center' }}>
                          <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                          <span className="order-no">{he ? 'הזמנה' : 'Order'} #{o.orderNumber}</span>
                          <span className="text-muted" style={{ fontSize: 12 }}>{fmtDate(o.sentAt || o.createdAt)}</span>
                          <span>{(o.shippingMethod || o.transportMode) ? (
                            <span className={`badge ${(o.shippingMethod || o.transportMode || '').toUpperCase() === 'AIR' ? 'badge-air' : 'badge-sea'}`}>
                              {(o.shippingMethod || o.transportMode || '').toUpperCase() === 'AIR' ? (he ? 'אוויר' : 'Air') : (he ? 'ים' : 'Ocean')}
                            </span>
                          ) : ''}</span>
                          <span className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>
                            {o.estimatedArrival ? `ETA: ${fmtDate(o.estimatedArrival)}` : ''}
                          </span>
                          <span>{o.customerStatus && (() => {
                            if (o.customerStatus === 'delivered') {
                              return <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#16a34a', color: '#fff', letterSpacing: 0.5 }}>✅ {he ? 'נמסר' : 'DELIVERED'}</span>;
                            }
                            const steps = ['confirmed','collecting','delivered_ff','shipped_customer'];
                            const labels = {confirmed: he ? 'אושר' : 'Confirmed', collecting: he ? 'נאסף' : 'Collecting', delivered_ff: he ? 'נמסר ל-FF' : 'Delivered to FF', shipped_customer: he ? 'נשלח ללקוח' : 'Shipped to Customer'};
                            const icons = {confirmed:'✓',collecting:'📦',delivered_ff:'🏭',shipped_customer:'🚢'};
                            const idx = steps.indexOf(o.customerStatus);
                            if (idx < 0) return null;
                            return (
                              <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                {steps.map((s, i) => (
                                  <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <div style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: 11, fontWeight: 700,
                                      background: i <= idx ? '#1565c0' : '#e2e8f0',
                                      color: i <= idx ? '#fff' : '#94a3b8' }}
                                      title={labels[s]}>
                                      {i <= idx ? icons[s] : (i + 1)}
                                    </div>
                                    {i < steps.length - 1 && <div style={{ width: 12, height: 2, background: i < idx ? '#1565c0' : '#e2e8f0' }} />}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}</span>
                          <span className="text-muted" style={{ fontSize: 12, textAlign: 'right' }}>{o.items.length} {he ? 'מוצרים' : 'products'} · {fmtQty(o.totalUnits)} {he ? 'יחידות' : 'units'}</span>
                          <span className="text-muted" style={{ fontSize: 11, textAlign: 'right' }}>{(() => {
                            const isAir = (o.shippingMethod || o.transportMode || '').toUpperCase() === 'AIR';
                            const totalCBM = o.totalCBM || 0;
                            const totalWeight = o.totalWeight || 0;
                            const PALLET_CBM = 1.0 * 1.2 * 2.05;
                            if (isAir) {
                              const masterCartons = o.items.reduce((s, i) => s + Math.ceil((i.quantity || 0) / (i.unitsPerMasterCarton || 1)), 0);
                              return `✈️ ${masterCartons} cartons · ${totalWeight.toFixed(1)} kg`;
                            } else if (totalCBM > 0) {
                              const pallets = Math.ceil(totalCBM / PALLET_CBM);
                              const containers = Math.ceil(pallets / 20);
                              return `🚢 ${containers}×40HC · ${pallets} pallets · ${totalWeight.toFixed(0)} kg`;
                            }
                            return '';
                          })()}</span>
                          <span className="fw-bold" style={{ textAlign: 'right' }}>{fmt(o.totalValue)}</span>
                        </div>
                        {isOpen && (
                          <div className="order-group-body table-wrap">
                            <table>
                              <thead><tr>
                                <th>{t('common.product')}</th><th>{t('common.sku')}</th><th className="text-end">{t('warehouse.qty')}</th>
                                <th className="text-end">{t('warehouse.price')}</th><th className="text-end">{t('common.total')}</th><th>{t('warehouse.transport')}</th>
                              </tr></thead>
                              <tbody>
                                {o.items.map((item, i) => (
                                  <tr key={i}>
                                    <td>{item.description}</td>
                                    <td className="text-muted" style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{item.sku}</td>
                                    <td className="text-end fw-bold">{fmtQty(item.quantity)}</td>
                                    <td className="text-end">{item.sellingPrice ? fmt(item.sellingPrice) : '—'}</td>
                                    <td className="text-end fw-bold">{item.sellingPrice ? fmt(item.sellingPrice * item.quantity) : '—'}</td>
                                    <td>{item.transportMode || '—'}</td>
                                  </tr>
                                ))}
                                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                                  <td colSpan={2}>{t('common.total')}</td>
                                  <td className="text-end">{fmtQty(o.totalUnits)}</td>
                                  <td></td>
                                  <td className="text-end">{fmt(o.totalValue)}</td>
                                  <td></td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })()}
        </>
      )}

      {/* ═══ Cellcom Orders (all orders with dates & shipping info) ═══ */}
      {tab === 'cellcom-orders' && (() => {
        const allOrders = [...shipped, ...future].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const toggleOrder = (num) => setExpanded(prev => { const s = new Set(prev); s.has(num) ? s.delete(num) : s.add(num); return s; });

        const filtered = searchTerm
          ? allOrders.filter(o => o.items.some(i =>
              i.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
              i.sku.toLowerCase().includes(searchTerm.toLowerCase())
            ) || String(o.orderNumber).toLowerCase().includes(searchTerm.toLowerCase()))
          : allOrders;

        return (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
              <input className="input" placeholder={he ? 'חפש הזמנות או מוצרים...' : 'Search orders or products...'} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ maxWidth: 300 }} />
              <span className="text-muted">{filtered.length} {he ? 'הזמנות' : 'orders'}</span>
            </div>

            {filtered.length === 0 ? (
              <div className="empty-state" style={{ padding: 40 }}>{t('warehouse.noOrders')}</div>
            ) : (
              filtered.map(o => {
                const isOpen = expanded.has(o.orderNumber);
                const statusColor = o.status === 'shipped' ? '#1565c0' : o.status === 'ready' ? '#4caf50' : '#ff9800';
                const statusBg = o.status === 'shipped' ? '#e3f2fd' : o.status === 'ready' ? '#e8f5e9' : '#fff3e0';
                const statusLabel = o.status === 'shipped' ? (he ? 'נשלח' : 'Shipped') : o.status === 'ready' ? (he ? 'מוכן' : 'Ready') : (he ? 'ממתין' : 'Pending');
                const isEditingThis = editingShipping?.orderNumber === o.orderNumber;

                return (
                  <div key={o.orderNumber} className="order-group" style={{ marginBottom: 8 }}>
                    <div className="order-group-header" onClick={() => toggleOrder(o.orderNumber)} style={{ cursor: 'pointer' }}>
                      <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                      <span className="order-no">{he ? 'הזמנה' : 'Order'} #{o.orderNumber}</span>
                      {o.approvalStatus === 'rejected' && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#fbe9e7', color: '#c62828' }}>{'✗ ' + t('warehouse.rejected')}</span>}
                      {(o.shippingMethod || o.transportMode) && (
                        <span className={`badge ${(o.shippingMethod || o.transportMode || '').toUpperCase() === 'AIR' ? 'badge-air' : 'badge-sea'}`}>
                          {(o.shippingMethod || o.transportMode || '').toUpperCase() === 'AIR' ? (he ? 'אוויר' : 'Air') : (he ? 'ים' : 'Ocean')}
                        </span>
                      )}
                      <div style={{ flex: 1 }} />
                      <span className="text-muted">{o.items.length} {he ? 'מוצרים' : 'products'} · {fmtQty(o.totalUnits)} {he ? 'יחידות' : 'units'}</span>
                      <span className="fw-bold" style={{ marginInlineStart: 12 }}>{fmt(o.totalValue)}</span>
                      <button className="btn btn-sm btn-outline" style={{ marginInlineStart: 8 }} onClick={(e) => {
                        e.stopPropagation();
                        const rows = o.items.map(item => ({
                          'SKU': item.sku,
                          'Barcode': item.barcode || '',
                          'Product': item.description,
                          'Quantity': parseFloat(item.quantity) || 0,
                          'Price': parseFloat(item.sellingPrice || item.selling_price) || 0,
                          'Total': (parseFloat(item.quantity) || 0) * (parseFloat(item.sellingPrice || item.selling_price) || 0),
                        }));
                        rows.push({ 'SKU': '', 'Product': 'TOTAL', 'Quantity': o.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0), 0), 'Price': '', 'Total': o.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.sellingPrice || i.selling_price) || 0), 0) });
                        const ws = XLSX.utils.json_to_sheet(rows);
                        ws['!cols'] = [{wch:12},{wch:35},{wch:10},{wch:10},{wch:12}];
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, `Order ${o.orderNumber}`);
                        XLSX.writeFile(wb, `order_${o.orderNumber}.xlsx`);
                      }}>⬇️ Excel</button>
                    </div>
                    {isOpen && (
                      <div className="order-group-body">
                        {/* Customer: Approve/Reject buttons */}
                        {o.approvalStatus === 'pending' && (
                          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: '#fff8e1', display: 'flex', gap: 12, alignItems: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#e65100' }}>{t('portal.requiresApproval')}</span>
                            <div style={{ flex: 1 }} />
                            <button className="btn btn-sm" style={{ background: '#4caf50', color: '#fff' }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm('Approve this order?')) return;
                                try { await api.put(`/portal/approve/${o.orderNumber}`); fetchData(true); }
                                catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                              }}>{'✓ ' + t('portal.approveOrder')}</button>
                            <button className="btn btn-sm" style={{ background: '#c62828', color: '#fff' }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                const reason = prompt('Reason for rejection (optional):');
                                if (reason === null) return;
                                try { await api.put(`/portal/reject/${o.orderNumber}`, { reason }); fetchData(true); }
                                catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                              }}>{'✗ ' + t('portal.rejectOrder')}</button>
                          </div>
                        )}

                        {/* Order info row */}
                        <div style={{ padding: '12px 16px', display: 'flex', gap: 24, flexWrap: 'wrap', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                          <div>
                            <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{t('portal.created')} </span>
                            <span>{fmtDate(o.createdAt)}</span>
                          </div>
                          <div>
                            <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{t('portal.estArrival')} </span>
                            <span style={{ color: o.estimatedArrival ? 'inherit' : '#999' }}>
                              {o.estimatedArrival ? fmtDate(o.estimatedArrival) : t('portal.notSet')}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{t('portal.shipping')} </span>
                            <span>{o.shippingMethod ? (o.shippingMethod.toUpperCase() === 'AIR' ? (he ? 'אוויר' : 'Air') : (he ? 'ים' : 'Ocean')) : t('portal.notSet')}</span>
                          </div>
                        </div>

                        {/* Admin: Edit shipping info */}
                        {isAdmin && (
                          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: '#fafafa' }}>
                            {!isEditingThis ? (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); setEditingShipping({ orderNumber: o.orderNumber, estimatedArrival: o.estimatedArrival ? o.estimatedArrival.split('T')[0] : '', shippingMethod: o.shippingMethod || '' }); }}>
                                  {t('warehouse.updateShipping')}
                                </button>
                                {o.status === 'shipped' && (
                                  <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); handleUnship(o.orderNumber); }}>
                                    {t('warehouse.cancelShipped')}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                                <select className="input" value={editingShipping.shippingMethod} onChange={e => setEditingShipping(prev => ({ ...prev, shippingMethod: e.target.value }))} style={{ width: 100 }}>
                                  <option value="">--</option>
                                  <option value="AIR">{he ? 'אוויר' : 'Air'}</option>
                                  <option value="SEA">{he ? 'ים' : 'Ocean'}</option>
                                </select>
                                <input className="input" type="date" value={editingShipping.estimatedArrival} onChange={e => setEditingShipping(prev => ({ ...prev, estimatedArrival: e.target.value }))} style={{ width: 160 }} />
                                <button className="btn btn-sm btn-gold" onClick={() => handleUpdateShipping(o.orderNumber)}>{he ? 'שמור' : 'Save'}</button>
                                <button className="btn btn-sm" onClick={() => setEditingShipping(null)}>{he ? 'ביטול' : 'Cancel'}</button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Items table */}
                        <div className="table-wrap">
                          <table>
                            <thead><tr>
                              <th>{he ? 'מוצר' : 'Product'}</th><th>{he ? 'מק"ט' : 'SKU'}</th><th className="text-end">{t('warehouse.qty')}</th>
                              <th className="text-end">{he ? 'מחיר' : 'Price'}</th><th className="text-end">{he ? 'סה"כ' : 'Total'}</th><th>{he ? 'שינוע' : 'Transport'}</th>
                            </tr></thead>
                            <tbody>
                              {o.items.map((item, i) => (
                                <tr key={i}>
                                  <td>{item.description}</td>
                                  <td className="text-muted" style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{item.sku}</td>
                                  <td className="text-end fw-bold">{fmtQty(item.quantity)}</td>
                                  <td className="text-end">{item.sellingPrice ? fmt(item.sellingPrice) : '—'}</td>
                                  <td className="text-end fw-bold">{item.sellingPrice ? fmt(item.sellingPrice * item.quantity) : '—'}</td>
                                  <td>{item.transportMode || '—'}</td>
                                </tr>
                              ))}
                              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                                <td colSpan={2}>{t('common.total')}</td>
                                <td className="text-end">{fmtQty(o.totalUnits)}</td>
                                <td></td>
                                <td className="text-end">{fmt(o.totalValue)}</td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        );
      })()}

      {/* ═══ Shipped / Future Orders (order-based, expandable) ═══ */}
      {/* ═══ FC Orders Tab ═══ */}
      {tab === 'future' && (() => {
        const toggleOrder = (num) => setExpanded(prev => { const s = new Set(prev); s.has(num) ? s.delete(num) : s.add(num); return s; });
        const hasEdits = Object.keys(fcEdits).length > 0;
        return (
          <div>
            <div className="flex-between" style={{ marginBottom: 16 }}>
              <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>
                {he ? 'הזמנות FC' : 'FC ORDERS'}
              </h2>
            </div>
            {fcOrders.length > 0 && (() => {
              const selectedItems = fcOrders.filter(o => selectedFcs.has(o.id)).flatMap(o => o.items || []);
              const summary = aggregateByModel(selectedItems, 'effectiveQty');
              if (selectedFcs.size === 0 || summary.length === 0) return null;
              return (
                <div style={{
                  position: 'sticky', top: 70, zIndex: 50,
                  background: 'linear-gradient(135deg,#dbeafe,#bfdbfe)',
                  border: '2px solid #1d4ed8', borderRadius: 8,
                  padding: 12, marginBottom: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 14 }}>
                      📊 {he ? `סיכום ${selectedFcs.size} הזמנות לפי מודל` : `Summary of ${selectedFcs.size} orders by model`}
                    </strong>
                    <div style={{ flex: 1 }} />
                    <span style={{ fontSize: 12, color: '#1e3a8a' }}>
                      {summary.reduce((s, m) => s + m.totalQty, 0).toLocaleString()} {he ? 'יח׳ סה"כ' : 'units total'}
                    </span>
                    <button className="btn btn-sm btn-outline" onClick={() => setSelectedFcs(new Set())} style={{ fontSize: 11 }}>
                      {he ? 'נקה' : 'Clear'}
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                    {summary.map(m => (
                      <div key={m.model} style={{ background: '#fff', padding: '8px 12px', borderRadius: 6, border: '1px solid #93c5fd' }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{m.model}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#1d4ed8' }}>
                          {m.totalQty.toLocaleString()} <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>{he ? 'יח׳' : 'units'}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>{m.lineCount} {he ? 'שורות' : 'lines'}</div>
                        {m.colors.length > 0 && (
                          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {m.colors.map(c => (
                              <div key={c.color} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, alignItems: 'baseline' }}>
                                <span style={{ color: '#475569' }}>{c.color}</span>
                                <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.qty.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {fcOrders.length === 0 ? <div className="empty-state">{he ? 'אין הזמנות FC' : 'No FC orders yet'}</div> :
              fcOrders.map(o => {
                const isOpen = expanded.has(`fc-${o.id}`);
                const isApproved = o.status === 'approved';
                const isSelected = selectedFcs.has(o.id);
                const totalOriginal = o.items.reduce((s, i) => s + i.originalQty * i.customerPrice, 0);
                const totalCustomer = o.items.reduce((s, i) => s + (i.customerQty != null ? i.customerQty : i.originalQty) * i.customerPrice, 0);
                return (
                  <div key={o.id} className="order-group" style={{ marginBottom: 8, outline: isSelected ? '2px solid var(--gold)' : 'none', borderRadius: 6 }}>
                    <div className="order-group-header" onClick={() => toggleOrder(`fc-${o.id}`)} style={{ cursor: 'pointer' }}>
                      <input type="checkbox" checked={isSelected}
                        onClick={e => e.stopPropagation()}
                        onChange={() => setSelectedFcs(prev => {
                          const next = new Set(prev);
                          next.has(o.id) ? next.delete(o.id) : next.add(o.id);
                          return next;
                        })}
                        style={{ marginInlineEnd: 8, width: 16, height: 16, cursor: 'pointer' }} />
                      <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                      <span className="order-no">{o.soOrderNo}</span>
                      {o.month && <span className="text-muted" style={{ fontSize: 12 }}>{o.month}</span>}
                      {o.estimatedArrival && <span className="text-muted" style={{ fontSize: 12 }}>ETA: {fmtDate(o.estimatedArrival)}</span>}
                      {isApproved ? (
                        <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#16a34a', color: '#fff' }}>✅ {he ? 'אושר' : 'Approved'}</span>
                      ) : (
                        <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e' }}>{he ? 'ממתין לאישור' : 'Pending Approval'}</span>
                      )}
                      <div style={{ flex: 1 }} />
                      <span className="text-muted">{o.items.length} {he ? 'מוצרים' : 'products'}</span>
                      <span className="fw-bold" style={{ marginInlineStart: 12 }}>{fmt(totalCustomer || totalOriginal)}</span>
                    </div>
                    {isOpen && (
                      <div className="order-group-body" style={{ padding: 16 }}>
                        {o.notes && <div className="text-muted" style={{ marginBottom: 12, fontSize: 13 }}>{o.notes}</div>}
                        <div className="table-wrap">
                          <table>
                            <thead><tr>
                              <th>{he ? 'מק"ט' : 'SKU'}</th>
                              <th>{he ? 'מוצר' : 'Product'}</th>
                              <th className="text-end">{he ? 'כמות מוצעת' : 'Proposed Qty'}</th>
                              <th className="text-end" style={{ background: '#fffbeb', borderBottom: '2px solid #f59e0b' }}>{he ? 'הכמות שלי' : 'My Qty'}</th>
                              <th className="text-end">{he ? 'מחיר' : 'Price'}</th>
                              <th className="text-end">{he ? 'סה"כ' : 'Total'}</th>
                            </tr></thead>
                            <tbody>
                              {o.items.map(item => {
                                const myQty = fcEdits[item.id] !== undefined ? fcEdits[item.id] : (item.customerQty != null ? item.customerQty : '');
                                const modified = item.customerModified || fcEdits[item.id] !== undefined;
                                const qty = fcEdits[item.id] !== undefined ? fcEdits[item.id] : (item.customerQty != null ? item.customerQty : item.originalQty);
                                return (
                                  <tr key={item.id} style={modified ? { background: '#fffbeb' } : {}}>
                                    <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{item.sku}{item.barcode && item.barcode !== item.sku && <div className="text-muted" style={{ fontSize: 10 }}>{item.barcode}</div>}</td>
                                    <td>{item.description}</td>
                                    <td className="text-end fw-bold">{fmtQty(item.originalQty)}</td>
                                    <td className="text-end" style={{ background: '#fffbeb' }}>
                                      {!isApproved ? (
                                        <input className="inline-edit" type="number" min="0"
                                          defaultValue={myQty} placeholder={String(item.originalQty)}
                                          key={`fc-${item.id}-${item.customerQty}-${o.status}`}
                                          onBlur={e => { const v = e.target.value; if (v !== '') setFcEdits(prev => ({ ...prev, [item.id]: parseInt(v) })); }}
                                          onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                                          style={{ width: 80, textAlign: 'right', fontWeight: modified ? 700 : 400 }} />
                                      ) : (
                                        <span style={{ fontWeight: 700, color: (item.customerQty || item.originalQty) !== item.originalQty ? '#e65100' : '#333' }}>
                                          {fmtQty(item.customerQty != null ? item.customerQty : item.originalQty)}
                                          {item.customerModified && <span style={{ fontSize: 10, color: '#6b7280' }}> (was {fmtQty(item.originalQty)})</span>}
                                        </span>
                                      )}
                                    </td>
                                    <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{item.customerPrice > 0 ? fmt(item.customerPrice) : '—'}</td>
                                    <td className="text-end fw-bold" style={{ fontFamily: "'DM Mono', monospace" }}>{fmt(qty * item.customerPrice)}</td>
                                  </tr>
                                );
                              })}
                              <tr style={{ fontWeight: 700, borderTop: '2px solid #dee2e6' }}>
                                <td colSpan={3}>{t('common.total')}</td>
                                <td className="text-end" style={{ background: '#fffbeb' }}>{fmtQty(o.items.reduce((s, i) => s + (fcEdits[i.id] !== undefined ? fcEdits[i.id] : (i.customerQty != null ? i.customerQty : i.originalQty)), 0))}</td>
                                <td></td>
                                <td className="text-end">{fmt(o.items.reduce((s, i) => { const q = fcEdits[i.id] !== undefined ? fcEdits[i.id] : (i.customerQty != null ? i.customerQty : i.originalQty); return s + q * i.customerPrice; }, 0))}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => {
                            const rows = o.items.map(item => {
                              const qty = item.customerQty != null ? item.customerQty : item.originalQty;
                              return {
                                'SKU': item.sku,
                                'Barcode': item.barcode || '',
                                'Product': item.description,
                                'Proposed Qty': item.originalQty,
                                'My Qty': qty,
                                'Price': item.customerPrice,
                                'Total': qty * item.customerPrice,
                              };
                            });
                            rows.push({});
                            rows.push({ 'SKU': '', 'Barcode': '', 'Product': 'TOTAL', 'Proposed Qty': o.items.reduce((s, i) => s + i.originalQty, 0), 'My Qty': o.items.reduce((s, i) => s + (i.customerQty != null ? i.customerQty : i.originalQty), 0), 'Price': '', 'Total': o.items.reduce((s, i) => s + (i.customerQty != null ? i.customerQty : i.originalQty) * i.customerPrice, 0) });
                            const ws = XLSX.utils.json_to_sheet(rows);
                            ws['!cols'] = [{wch:12},{wch:16},{wch:35},{wch:12},{wch:10},{wch:10},{wch:12}];
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, `FC ${o.soOrderNo}`);
                            XLSX.writeFile(wb, `FC_${o.soOrderNo}_${o.month || ''}.xlsx`);
                          }}>📥 Excel</button>
                        </div>
                        {!isApproved && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                            {hasEdits && <button className="btn btn-outline btn-sm" onClick={() => setFcEdits({})}>{he ? 'נקה שינויים' : 'Clear Changes'}</button>}
                            <button className="btn btn-gold" onClick={async () => {
                              const items = o.items.map(item => ({
                                itemId: item.id,
                                customerQty: fcEdits[item.id] !== undefined ? fcEdits[item.id] : (item.customerQty != null ? item.customerQty : item.originalQty),
                              }));
                              try {
                                await api.put(`/portal/fc-orders/${o.id}/approve`, { items });
                                setFcEdits({});
                                const r = await api.get('/portal/fc-orders');
                                setFcOrders(r.data || []);
                              } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                            }}>
                              {he ? 'אשר הזמנה' : 'Approve Order'}
                            </button>
                          </div>
                        )}
                        {isApproved && (
                          <div style={{ marginTop: 12, padding: 12, background: '#dcfce7', borderRadius: 8, textAlign: 'center', fontWeight: 600, color: '#166534' }}>
                            ✅ {he ? `אושר ב-${fmtDate(o.customerApprovedAt)}` : `Approved on ${fmtDate(o.customerApprovedAt)}`}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            }
          </div>
        );
      })()}

      {tab === 'shipped' && (() => {
        const orders = shipped;
        const title = he ? 'הזמנות שנשלחו' : 'Shipped Orders';
        const toggleOrder = (num) => setExpanded(prev => { const s = new Set(prev); s.has(num) ? s.delete(num) : s.add(num); return s; });

        // Filter by search
        const filtered = searchTerm
          ? orders.filter(o => o.items.some(i =>
              i.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
              i.sku.toLowerCase().includes(searchTerm.toLowerCase())
            ))
          : orders;

        const totalUnitsAll = filtered.reduce((s, o) => s + o.totalUnits, 0);
        const totalValueAll = filtered.reduce((s, o) => s + o.totalValue, 0);

        return (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
              <input className="input" placeholder={t('warehouse.searchProducts')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                style={{ maxWidth: 300 }} />
              <span className="text-muted">{filtered.length} {he ? 'הזמנות' : 'orders'} · {fmtQty(totalUnitsAll)} {he ? 'יחידות' : 'units'} · {fmt(totalValueAll)}</span>
            </div>

            {filtered.length === 0 ? (
              <div className="empty-state" style={{ padding: 40 }}>{he ? 'אין תוצאות' : `No ${title.toLowerCase()}`}</div>
            ) : (
              filtered.map(o => {
                const isOpen = expanded.has(o.orderNumber);
                const statusColor = o.status === 'shipped' ? '#1565c0' : o.status === 'ready' ? '#4caf50' : '#ff9800';
                const statusBg = o.status === 'shipped' ? '#e3f2fd' : o.status === 'ready' ? '#e8f5e9' : '#fff3e0';
                const statusLabel = o.status === 'shipped' ? (he ? 'נשלח' : 'Shipped') : o.status === 'ready' ? (he ? 'מוכן' : 'Ready') : (he ? 'ממתין' : 'Pending');
                const isEditingThis = editingShipping?.orderNumber === o.orderNumber;
                return (
                  <div key={o.orderNumber} className="order-group" style={{ marginBottom: 8 }}>
                    <div className="order-group-header" onClick={() => toggleOrder(o.orderNumber)} style={{ cursor: 'pointer' }}>
                      <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                      <span className="order-no">{he ? 'הזמנה' : 'Order'} #{o.orderNumber}</span>
                      <span className="text-muted">{fmtDate(o.sentAt || o.createdAt)}</span>
                      {(o.shippingMethod || o.transportMode) && (
                        <span className={`badge ${(o.shippingMethod || o.transportMode || '').toUpperCase() === 'AIR' ? 'badge-air' : 'badge-sea'}`}>
                          {(o.shippingMethod || o.transportMode || '').toUpperCase() === 'AIR' ? (he ? 'אוויר' : 'Air') : (he ? 'ים' : 'Ocean')}
                        </span>
                      )}
                      <div style={{ flex: 1 }} />
                      {o.estimatedArrival && <span className="text-muted" style={{ fontSize: 12 }}>ETA: {fmtDate(o.estimatedArrival)}</span>}
                      <span className="text-muted">{o.items.length} {he ? 'מוצרים' : 'products'} · {fmtQty(o.totalUnits)} {he ? 'יחידות' : 'units'}</span>
                      <span className="fw-bold" style={{ marginInlineStart: 12 }}>{fmt(o.totalValue)}</span>
                    </div>
                    {isOpen && (
                      <div className="order-group-body">
                        {/* Order info row */}
                        <div style={{ padding: '12px 16px', display: 'flex', gap: 24, flexWrap: 'wrap', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                          <div>
                            <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{t('portal.created')} </span>
                            <span>{fmtDate(o.createdAt)}</span>
                          </div>
                          <div>
                            <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{t('portal.estArrival')} </span>
                            <span style={{ color: o.estimatedArrival ? 'inherit' : '#999' }}>
                              {o.estimatedArrival ? fmtDate(o.estimatedArrival) : t('portal.notSet')}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{t('portal.shipping')} </span>
                            <span>{o.shippingMethod ? (o.shippingMethod.toUpperCase() === 'AIR' ? (he ? 'אוויר' : 'Air') : (he ? 'ים' : 'Ocean')) : (o.transportMode || t('portal.notSet'))}</span>
                          </div>
                        </div>

                        {/* Admin controls */}
                        {isAdmin && (
                          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: '#fafafa' }}>
                            {!isEditingThis ? (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); setEditingShipping({ orderNumber: o.orderNumber, estimatedArrival: o.estimatedArrival ? o.estimatedArrival.split('T')[0] : '', shippingMethod: o.shippingMethod || '' }); }}>
                                  {t('warehouse.updateShipping')}
                                </button>
                                {o.status === 'shipped' && (
                                  <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); handleUnship(o.orderNumber); }}>
                                    {t('warehouse.cancelShipped')}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                                <select className="input" value={editingShipping.shippingMethod} onChange={e => setEditingShipping(prev => ({ ...prev, shippingMethod: e.target.value }))} style={{ width: 100 }}>
                                  <option value="">--</option>
                                  <option value="AIR">{he ? 'אוויר' : 'Air'}</option>
                                  <option value="SEA">{he ? 'ים' : 'Ocean'}</option>
                                </select>
                                <input className="input" type="date" value={editingShipping.estimatedArrival} onChange={e => setEditingShipping(prev => ({ ...prev, estimatedArrival: e.target.value }))} style={{ width: 160 }} />
                                <button className="btn btn-sm btn-gold" onClick={() => handleUpdateShipping(o.orderNumber)}>{he ? 'שמור' : 'Save'}</button>
                                <button className="btn btn-sm" onClick={() => setEditingShipping(null)}>{he ? 'ביטול' : 'Cancel'}</button>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="table-wrap">
                          <table>
                            <thead><tr>
                              <th>{he ? 'מוצר' : 'Product'}</th><th>{he ? 'מק"ט' : 'SKU'}</th><th className="text-end">{t('warehouse.qty')}</th>
                              <th className="text-end">{he ? 'מחיר' : 'Price'}</th><th className="text-end">{he ? 'סה"כ' : 'Total'}</th><th>{he ? 'שינוע' : 'Transport'}</th>
                            </tr></thead>
                            <tbody>
                              {o.items.map((item, i) => (
                                <tr key={i}>
                                  <td>{item.description}</td>
                                  <td className="text-muted" style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{item.sku}</td>
                                  <td className="text-end fw-bold">{fmtQty(item.quantity)}</td>
                                  <td className="text-end">{item.sellingPrice ? fmt(item.sellingPrice) : '—'}</td>
                                  <td className="text-end fw-bold">{item.sellingPrice ? fmt(item.sellingPrice * item.quantity) : '—'}</td>
                                  <td>{item.transportMode || '—'}</td>
                                </tr>
                              ))}
                              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                                <td colSpan={2}>{t('common.total')}</td>
                                <td className="text-end">{fmtQty(o.totalUnits)}</td>
                                <td></td>
                                <td className="text-end">{fmt(o.totalValue)}</td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        );
      })()}

      {/* ═══ Credits ═══ */}
      {tab === 'credits' && (
        <div>
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-dim)' }}>
                {he ? 'זיכויים' : 'Credits'} — {new Date().getFullYear()}
              </h3>
              <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--success)' }}>{fmt(credits.totalCredits)}</span>
            </div>
            {credits.credits.length === 0 ? (
              <div className="empty-state" style={{ padding: 40 }}>{t('portal.noCredits')}</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th>{he ? 'תאריך' : 'Date'}</th><th>{he ? 'תיאור' : 'Description'}</th><th className="text-end">{he ? 'סכום' : 'Amount'}</th>
                    {isAdmin && <th></th>}
                  </tr></thead>
                  <tbody>
                    {credits.credits.map((c) => (
                      <tr key={`credit-${c.id}`}>
                        <td>{fmtDate(c.date)}</td>
                        <td>{c.description}{c.notes && <span className="text-muted" style={{ fontSize: 12, marginInlineStart: 8 }}>({c.notes})</span>}</td>
                        <td className="text-end fw-bold" style={{ color: 'var(--success)' }}>{fmt(c.amount)}</td>
                        {isAdmin && <td><button className="btn btn-sm btn-danger" onClick={async () => { if (window.confirm(he ? 'למחוק זיכוי זה?' : 'Delete this credit?')) { await api.delete(`/portal/credits/${c.id}`); fetchData(true); } }}>{he ? 'מחק' : 'Delete'}</button></td>}
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                      <td colSpan={2}>{t('portal.totalCredits')}</td>
                      <td className="text-end" style={{ color: 'var(--success)' }}>{fmt(credits.totalCredits)}</td>
                      {isAdmin && <td></td>}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      )}

      {/* ═══ Rebate ═══ */}
      {tab === 'rebate' && rebate && (
        <div>
          <div className="summary-cards" style={{ marginBottom: 24 }}>
            <div className="summary-card gold">
              <div className="label">{t('portal.totalOrderValue')}</div>
              <div className="value">{fmt(rebate.totalOrderValue)}</div>
            </div>
            <div className="summary-card" style={{ borderLeft: '4px solid var(--danger)' }}>
              <div className="label">{t('portal.credits')}</div>
              <div className="value" style={{ color: 'var(--danger)' }}>-{fmt(rebate.totalCredits)}</div>
            </div>
            <div className="summary-card info">
              <div className="label">{t('portal.rebateBase')}</div>
              <div className="value">{fmt(rebate.rebateBase)}</div>
            </div>
            <div className="summary-card success">
              <div className="label">{t('portal.rebate') + ' (1%)'}</div>
              <div className="value" style={{ fontSize: 28 }}>{fmt(rebate.rebateAmount)}</div>
            </div>
          </div>

          <div className="card">
            <div style={{ padding: 30, textAlign: 'center' }}>
              <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-dim)', marginBottom: 8 }}>
                {t('portal.annualRebate')}
              </div>
              <div style={{ fontSize: 48, fontWeight: 800, color: 'var(--success)', marginBottom: 8 }}>
                {fmt(rebate.rebateAmount)}
              </div>
              <div style={{ fontSize: 14, color: 'var(--text-dim)' }}>
                ({fmt(rebate.totalOrderValue)} - {fmt(rebate.totalCredits)}) x 1% = {fmt(rebate.rebateAmount)}
              </div>
              <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text-dim)' }}>
                {t('portal.updatedLive')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Price List ═══ */}
      {tab === 'price-list' && (() => {
        const searchLower = priceSearch.toLowerCase();
        const filtered = priceList.filter(p => !searchLower ||
          (p.description || '').toLowerCase().includes(searchLower) ||
          (p.sku || '').toLowerCase().includes(searchLower) ||
          (p.barcode || '').toLowerCase().includes(searchLower)
        );
        const priceCell = (current, prev) => {
          if (current == null) return <span className="text-muted">—</span>;
          const changed = prev != null && prev !== current;
          const down = changed && current < prev;
          const up = changed && current > prev;
          return (
            <span style={{ fontWeight: 700, color: down ? '#16a34a' : up ? '#dc2626' : '#333' }}>
              ${current.toFixed(2)}
              {down && <span style={{ fontSize: 10, marginInlineStart: 4 }}>▼</span>}
              {up && <span style={{ fontSize: 10, marginInlineStart: 4 }}>▲</span>}
              {changed && <div className="text-muted" style={{ fontSize: 10, fontWeight: 400, textDecoration: 'line-through' }}>${prev.toFixed(2)}</div>}
            </span>
          );
        };
        const saveWish = async (sku, field, value) => {
          const val = parseFloat(value) || null;
          const item = priceList.find(p => p.sku === sku);
          if (!item) return;
          const update = field === 'air' ? { wishAirPrice: val, wishOceanPrice: item.wishOceanPrice } : { wishAirPrice: item.wishAirPrice, wishOceanPrice: val };
          await api.put('/portal/wish-price', { sku, ...update });
          setPriceList(prev => prev.map(p => p.sku === sku ? { ...p, ...update } : p));
        };
        return (
          <div>
            <div className="flex-between" style={{ marginBottom: 16 }}>
              <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>
                {he ? 'מחירון' : 'PRICE LIST'}
              </h2>
              <input className="input" placeholder={he ? 'חפש מוצר, מק"ט...' : 'Search product, SKU...'}
                value={priceSearch} onChange={e => setPriceSearch(e.target.value)} style={{ maxWidth: 300 }} />
            </div>
            {filtered.length === 0 ? <div className="empty-state">{t('common.noData')}</div> : (
              <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th>{he ? 'מק"ט' : 'SKU'}</th>
                    <th>{he ? 'מוצר' : 'Product'}</th>
                    <th className="text-end">✈️ {he ? 'אוויר' : 'Air'}</th>
                    <th className="text-end">🚢 {he ? 'ים' : 'Ocean'}</th>
                    <th className="text-end" style={{ background: '#fffbeb', borderBottom: '2px solid #f59e0b' }}>✈️ {he ? 'מחיר מבוקש' : 'Wish'}</th>
                    <th className="text-end" style={{ background: '#fffbeb', borderBottom: '2px solid #f59e0b' }}>🚢 {he ? 'מחיר מבוקש' : 'Wish'}</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(p => (
                      <tr key={p.sku}>
                        <td>
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{p.sku}</div>
                          {p.barcode && p.barcode !== p.sku && <div className="text-muted" style={{ fontSize: 10 }}>{p.barcode}</div>}
                        </td>
                        <td>{p.description}</td>
                        <td className="text-end">{priceCell(p.airPrice, p.prevAirPrice)}</td>
                        <td className="text-end">{priceCell(p.oceanPrice, p.prevOceanPrice)}</td>
                        <td className="text-end" style={{ background: '#fffbeb' }}>
                          <input className="inline-edit" type="number" step="0.01"
                            style={{ width: 80, textAlign: 'right', fontSize: 13, background: p.wishAirPrice ? '#fef3c7' : 'transparent' }}
                            defaultValue={p.wishAirPrice || ''} placeholder="—"
                            onBlur={e => saveWish(p.sku, 'air', e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
                        </td>
                        <td className="text-end" style={{ background: '#fffbeb' }}>
                          <input className="inline-edit" type="number" step="0.01"
                            style={{ width: 80, textAlign: 'right', fontSize: 13, background: p.wishOceanPrice ? '#fef3c7' : 'transparent' }}
                            defaultValue={p.wishOceanPrice || ''} placeholder="—"
                            onBlur={e => saveWish(p.sku, 'ocean', e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ RMA Request ═══ */}
      {tab === 'rma' && (
        <div>
          <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>
            {he ? 'בקשת RMA — מוצרים פגומים' : 'RMA REQUEST — Defective Products'}
          </h2>

          {/* New RMA form */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-dim)' }}>
                {he ? 'בקשה חדשה' : 'New Request'}
              </h3>
            </div>
            <div style={{ padding: 16 }}>
              <div className="table-wrap" style={{ marginBottom: 12 }}>
                <table>
                  <thead><tr>
                    <th>{he ? 'מק"ט' : 'SKU'}</th>
                    <th>{he ? 'מוצר' : 'Product'}</th>
                    <th style={{ width: 80 }}>{he ? 'כמות' : 'Qty'}</th>
                    <th>{he ? 'סיבה / תיאור הבעיה' : 'Reason / Issue'}</th>
                    <th style={{ width: 40 }}></th>
                  </tr></thead>
                  <tbody>
                    {rmaItems.map((item, i) => (
                      <tr key={i}>
                        <td><input className="input input-sm" placeholder={he ? 'מק"ט...' : 'SKU...'} value={item.sku} onChange={e => setRmaItems(prev => prev.map((it, j) => j === i ? { ...it, sku: e.target.value } : it))} style={{ width: 100 }} /></td>
                        <td><input className="input input-sm" placeholder={he ? 'שם מוצר...' : 'Product name...'} value={item.description} onChange={e => setRmaItems(prev => prev.map((it, j) => j === i ? { ...it, description: e.target.value } : it))} /></td>
                        <td><input className="input input-sm" type="number" min="1" value={item.quantity} onChange={e => setRmaItems(prev => prev.map((it, j) => j === i ? { ...it, quantity: parseInt(e.target.value) || 1 } : it))} style={{ width: 70, textAlign: 'center' }} /></td>
                        <td><input className="input input-sm" placeholder={he ? 'תיאור הבעיה...' : 'Describe the issue...'} value={item.reason} onChange={e => setRmaItems(prev => prev.map((it, j) => j === i ? { ...it, reason: e.target.value } : it))} /></td>
                        <td>{rmaItems.length > 1 && <button className="btn btn-sm" style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }} onClick={() => setRmaItems(prev => prev.filter((_, j) => j !== i))}>✕</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-sm btn-outline" onClick={() => setRmaItems(prev => [...prev, { sku: '', description: '', quantity: 1, reason: '' }])}>
                  + {he ? 'הוסף שורה' : 'Add Row'}
                </button>
                <label className="btn btn-sm btn-outline" style={{ cursor: 'pointer' }}>
                  📥 {he ? 'העלה אקסל' : 'Upload Excel'}
                  <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const wb = XLSX.read(ev.target.result, { type: 'binary' });
                      const ws = wb.Sheets[wb.SheetNames[0]];
                      const rows = XLSX.utils.sheet_to_json(ws);
                      const items = rows.filter(r => r.SKU || r.sku || r.Product || r.product).map(r => ({
                        sku: String(r.SKU || r.sku || r['מק"ט'] || ''),
                        description: r.Product || r.product || r.Description || r.description || r['מוצר'] || '',
                        quantity: parseInt(r.Qty || r.qty || r.Quantity || r.quantity || r['כמות']) || 1,
                        reason: r.Reason || r.reason || r.Issue || r.issue || r['סיבה'] || '',
                      }));
                      if (items.length) setRmaItems(items);
                      else alert(he ? 'לא נמצאו שורות. צריך עמודות: SKU, Product, Qty, Reason' : 'No valid rows. Need columns: SKU, Product, Qty, Reason');
                    };
                    reader.readAsBinaryString(file);
                    e.target.value = '';
                  }} />
                </label>
                <div style={{ flex: 1 }} />
                <input className="input input-sm" placeholder={he ? 'הערות כלליות...' : 'General notes...'} value={rmaNotes} onChange={e => setRmaNotes(e.target.value)} style={{ maxWidth: 250 }} />
                <button className="btn btn-gold" disabled={rmaSubmitting || rmaItems.every(i => !i.sku && !i.description)} onClick={async () => {
                  const valid = rmaItems.filter(i => i.sku || i.description);
                  if (!valid.length) return;
                  setRmaSubmitting(true);
                  try {
                    await api.post('/portal/rma', { items: valid, notes: rmaNotes });
                    setRmaItems([{ sku: '', description: '', quantity: 1, reason: '' }]);
                    setRmaNotes('');
                    const r = await api.get('/portal/rma');
                    setRmaRequests(r.data || []);
                  } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                  finally { setRmaSubmitting(false); }
                }}>
                  {rmaSubmitting ? '...' : (he ? 'שלח בקשה' : 'Submit RMA')}
                </button>
              </div>
            </div>
          </div>

          {/* Previous RMA requests */}
          {rmaRequests.length > 0 && (
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 12 }}>
                {he ? 'בקשות קודמות' : 'Previous Requests'}
              </h3>
              {rmaRequests.map(rma => (
                <div key={rma.id} className="order-group" style={{ marginBottom: 8 }}>
                  <div className="order-group-header" onClick={() => setExpanded(prev => { const s = new Set(prev); s.has(`rma-${rma.id}`) ? s.delete(`rma-${rma.id}`) : s.add(`rma-${rma.id}`); return s; })} style={{ cursor: 'pointer' }}>
                    <span className={`arrow${expanded.has(`rma-${rma.id}`) ? ' open' : ''}`}>&#9654;</span>
                    <span className="fw-bold">RMA #{rma.id}</span>
                    <span className="text-muted" style={{ fontSize: 12 }}>{fmtDate(rma.created_at)}</span>
                    <span style={{
                      padding: '2px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                      background: rma.status === 'approved' ? '#dcfce7' : rma.status === 'rejected' ? '#fee2e2' : '#f1f5f9',
                      color: rma.status === 'approved' ? '#166534' : rma.status === 'rejected' ? '#991b1b' : '#475569',
                    }}>{rma.status.toUpperCase()}</span>
                    {rma.customer_name && !isCustomer && <span className="text-muted" style={{ fontSize: 12 }}>{rma.customer_name}</span>}
                    <div style={{ flex: 1 }} />
                    <span className="text-muted">{(rma.items || []).length} {he ? 'פריטים' : 'items'}</span>
                    {isAdmin && (
                      <span onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
                        {['pending', 'approved', 'rejected'].map(s => (
                          <button key={s} style={{
                            padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                            border: rma.status === s ? 'none' : '1px solid #e2e8f0',
                            background: rma.status === s ? (s === 'approved' ? '#16a34a' : s === 'rejected' ? '#dc2626' : '#64748b') : '#fff',
                            color: rma.status === s ? '#fff' : '#64748b',
                          }} onClick={async () => {
                            await api.put(`/portal/rma/${rma.id}/status`, { status: s });
                            const r = await api.get('/portal/rma');
                            setRmaRequests(r.data || []);
                          }}>{s}</button>
                        ))}
                      </span>
                    )}
                  </div>
                  {expanded.has(`rma-${rma.id}`) && (
                    <div className="order-group-body">
                      {rma.notes && <div className="text-muted" style={{ padding: '8px 16px', fontSize: 13 }}>{rma.notes}</div>}
                      <div className="table-wrap">
                        <table>
                          <thead><tr>
                            <th>{he ? 'מק"ט' : 'SKU'}</th>
                            <th>{he ? 'מוצר' : 'Product'}</th>
                            <th className="text-end">{he ? 'כמות' : 'Qty'}</th>
                            <th>{he ? 'סיבה' : 'Reason'}</th>
                          </tr></thead>
                          <tbody>
                            {(rma.items || []).map((item, i) => (
                              <tr key={i}>
                                <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{item.sku || '—'}</td>
                                <td>{item.description || '—'}</td>
                                <td className="text-end fw-bold">{item.quantity}</td>
                                <td>{item.reason || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
